/** @module Components */

import React, { JSX, useState } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import { asyncDebounce } from '@tanstack/pacer'

type GlobalFilterProps = {
  globalFilter: string | undefined
  setGlobalFilter: (filterValue: string) => void
}

export const SearchBar: React.FC<GlobalFilterProps> = ({
  globalFilter,
  setGlobalFilter
}: GlobalFilterProps): JSX.Element => {
  const [searchValue, setSearchValue] = useState(globalFilter)

  const handleInput = asyncDebounce(
    async (value) => {
      setGlobalFilter(value || '')
    },
    {
      wait: 300,
      leading: true,
      trailing: true
    }
  )

  return (
    <div
      className="flex flex-col justify-center static w-full md:w-1/3 h-full mx-8 pb-5"
      data-testid="search-bar-container"
    >
      <div className="w-full flex border-2 border-[#9298a8] rounded-md overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-200">
        <label
          htmlFor="search-field"
          className="sr-only"
          id="screen-reader-search-label"
        >
          Search
        </label>
        <div className="relative w-full text-[#142841] focus-within:text-[#A6410A]">
          <div
            className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-3"
            aria-hidden="true"
          >
            <MagnifyingGlassIcon
              className="h-5 w-5"
              aria-hidden="true"
              id="search-icon"
              data-testid="search-icon"
            />
          </div>
          <input
            id="search-field"
            name="search_field"
            className="block w-full h-full pl-10 pr-3 py-2.5 border-transparent text-[#3C2416] placeholder-[#713d43] bg-[#AF8A48]/15 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#A6410A] focus:border-transparent sm:text-sm"
            placeholder="Search..."
            value={searchValue || ''}
            onChange={(event) => {
              setSearchValue(event.target.value)
              handleInput(event.target.value)
            }}
            type="search"
            data-testid="search-input"
          />
        </div>
      </div>
    </div>
  )
}
